import axios from 'axios';
import { Notification } from 'element-ui';

class VersionChecker {
  constructor() {
    this.currentVersion = null;  // 当前版本号
    this.timer = null;           // 定时器
    this.checkInterval = 5 * 60 * 1000; // 5分钟检查一次
    this.updateDialogShown = false; // 标记是否已经显示过更新提示
    this.lastCheckTime = 0;      // 上次检查时间
  }

  /**
   * 从服务器获取最新版本号
   * 注意：使用完整URL避免被axios的baseURL影响
   */
  async fetchServerVersion() {
    try {
      // 使用当前域名 + /version.json，避免跨域
      const versionUrl = `${window.location.origin}/version.json?t=${Date.now()}`;
      
      const response = await axios.get(versionUrl, {
        timeout: 5000,
        // 不使用全局的baseURL配置
        baseURL: '',
      });
      // 统一转换为字符串，避免类型比较问题
      return String(response.data.version);
    } catch (error) {
      console.error('获取版本信息失败:', error);
      return null;
    }
  }

  /**
   * 初始化版本检测
   * 使用 localStorage 记录初始版本号，避免刷新时版本号更新导致检测失效
   */
  async init() {
    // 1. 先检查 localStorage 中是否有记录的版本号
    const storedVersion = localStorage.getItem('app_version');
    
    // 2. 检查是否已显示过更新提示（持久化标记）
    const updateShown = localStorage.getItem('update_dialog_shown');
    if (updateShown === 'true') {
      this.updateDialogShown = true;
      console.log('⚠️ 已显示过更新提示，不再重复');
    }
    
    // 3. 获取服务器最新版本号
    const serverVersion = await this.fetchServerVersion();
    
    if (!serverVersion) {
      console.warn('⚠ 无法获取版本信息');
      return;
    }

    // 4. 如果 localStorage 中没有版本号，说明是第一次访问或清除了缓存
    if (!storedVersion) {
      // 记录当前版本号
      localStorage.setItem('app_version', serverVersion);
      this.currentVersion = serverVersion;
      console.log('✔ 首次访问，记录版本:', serverVersion);
      // 清除更新提示标记（新版本，重新开始）
      localStorage.removeItem('update_dialog_shown');
      this.updateDialogShown = false;
    } else {
      // 使用 localStorage 中的版本号作为"当前版本"
      this.currentVersion = storedVersion;
      console.log('✔ 当前版本:', this.currentVersion);
      console.log('✔ 服务器版本:', serverVersion);
      
      // 立即检查是否有更新
      if (serverVersion !== this.currentVersion) {
        // 检查是否已经提示过
        if (!this.updateDialogShown) {
          console.log('🎉 发现新版本!');
          this.updateDialogShown = true;
          // 持久化标记，避免刷新后重复提示
          localStorage.setItem('update_dialog_shown', 'true');
          this.showUpdateDialog();
        } else {
          console.log('⚠️ 已提示过更新，等待用户操作');
        }
        return; // 发现更新后不再启动定时器
      } else {
        // 版本一致，清除更新提示标记
        localStorage.removeItem('update_dialog_shown');
        this.updateDialogShown = false;
      }
    }

    // 5. 开始定期检查
    this.startCheck();
  }

  /**
   * 开始定期检查更新
   */
  startCheck() {
    // 清除旧定时器
    if (this.timer) {
      clearInterval(this.timer);
    }
    
    // 每5分钟检查一次
    this.timer = setInterval(async () => {
      await this.checkUpdate();
    }, this.checkInterval);
    
    console.log('✔ 版本检测已启动，每5分钟检查一次');
  }

  /**
   * 检查是否有新版本
   */
  async checkUpdate() {
    // 如果已经显示过更新提示，不再重复检查
    if (this.updateDialogShown) {
      console.log('已显示过更新提示，跳过检查');
      return;
    }
    
    // 防止频繁检查：距离上次检查不到1分钟，跳过
    const now = Date.now();
    if (now - this.lastCheckTime < 60 * 1000) {
      console.log('距离上次检查时间过短，跳过本次检查');
      return;
    }
    this.lastCheckTime = now;
    
    const serverVersion = await this.fetchServerVersion();
    
    // 如果服务器版本号和当前版本号不一致，说明有更新
    if (serverVersion && serverVersion !== this.currentVersion) {
      console.log('🎉 发现新版本!');
      console.log('   当前版本:', this.currentVersion);
      console.log('   最新版本:', serverVersion);
      
      // 标记已显示更新提示（内存 + 持久化）
      this.updateDialogShown = true;
      localStorage.setItem('update_dialog_shown', 'true');
      
      // 显示更新提示
      this.showUpdateDialog();
      
      // 停止检查，避免重复提示
      this.stopCheck();
    }
  }

  /**
   * 显示更新提示通知（右下角）
   */
  showUpdateDialog() {
    // 创建右下角通知
    const notification = Notification({
      title: '发现新版本 🎉',
      dangerouslyUseHTMLString: true,
      message: `
        <div style="line-height: 1.6;">
          <p style="margin-bottom: 15px; color: #606266;">检测到系统有新版本，建议立即更新以获得更好体验。</p>
          <div style="text-align: right;">
            <button 
              class="el-button el-button--default el-button--small" 
              style="margin-right: 10px;"
              onclick="window.versionUpdatePostpone()">
              稍后更新
            </button>
            <button 
              class="el-button el-button--primary el-button--small"
              onclick="window.versionUpdateNow()">
              立即更新
            </button>
          </div>
        </div>
      `,
      duration: 0,  // 不自动关闭
      position: 'bottom-right',  // 右下角显示
      showClose: true,
      type: 'warning',
      customClass: 'version-update-notification',
      onClose: () => {
        console.log('用户关闭了更新提示');
        // 清理全局方法
        delete window.versionUpdateNow;
        delete window.versionUpdatePostpone;
      }
    });

    // 创建全局方法供按钮调用
    window.versionUpdateNow = () => {
      console.log('用户选择立即更新');
      notification.close();
      this.forceReload();
    };

    window.versionUpdatePostpone = () => {
      console.log('用户选择稍后更新');
      notification.close();
      // 10分钟后再次提醒
      setTimeout(() => {
        this.showUpdateDialog();
      }, 10 * 60 * 1000);
    };
  }

  /**
   * 强制刷新页面
   * 清除所有缓存并重新加载
   */
  async forceReload() {
    console.log('开始清除缓存并刷新页面...');
    
    // 1. 先获取最新版本号，等待保存完成
    try {
      const newVersion = await this.fetchServerVersion();
      if (newVersion) {
        // 更新为最新版本号，避免刷新后再次提示
        localStorage.setItem('app_version', newVersion);
        // 清除更新提示标记（已更新到新版本）
        localStorage.removeItem('update_dialog_shown');
        console.log('✅ 已更新版本号:', newVersion);
        console.log('✅ 已清除更新提示标记');
      }
    } catch (error) {
      console.error('获取版本号失败:', error);
    }
    
    // 2. 清除浏览器缓存（Service Worker缓存）
    if (window.caches) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map(name => {
          console.log('清除缓存:', name);
          return caches.delete(name);
        }));
        console.log('✅ 缓存已清除');
      } catch (error) {
        console.error('清除缓存失败:', error);
      }
    }
    
    // 3. 清除本地存储（可选，根据需求）
    // 注意：不要取消注释下面这两行，否则会清除 token 等重要信息
    // localStorage.clear();
    // sessionStorage.clear();
    
    // 4. 强制刷新页面（最可靠的方法）
    setTimeout(() => {
      console.log('🔄 执行强制刷新...');
      
      // 使用最直接的刷新方式，确保所有资源重新加载
      // 由于已经清除了缓存，reload() 会从服务器重新下载所有资源
      window.location.reload();
    }, 500);
  }

  /**
   * 停止版本检查
   */
  stopCheck() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
      console.log('版本检测已停止');
    }
  }

  /**
   * 手动检查更新（可在设置页面调用）
   */
  async manualCheck() {
    console.log('手动检查更新...');
    const serverVersion = await this.fetchServerVersion();
    
    if (serverVersion && serverVersion !== this.currentVersion) {
      this.showUpdateDialog();
    } else {
      MessageBox.alert(
        `当前已是最新版本\n版本号: ${this.currentVersion}`,
        '版本检查',
        {
          confirmButtonText: '确定',
          type: 'success',
        }
      );
    }
  }
}

// 导出单例
export default new VersionChecker();


// WEBPACK FOOTER //
// ./src/utils/versionCheck.js